import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { RappiderEditFormModule } from '@rappider/rappider-components';
import { EntityService } from '../../services/entity.service';
import { CrudFormConfig, CrudViewFormItemType } from '@rappider/rappider-components/utils';
import { DATA_SOURCE_CREATE_BY_FILE_META_CONFIG } from '../../configs/data-source.entity.config';
import { LoopbackCrudApiService } from '../../services/loopback-crud-api.service';
import { DataSourceControllerService } from 'libs/rappider-sdk/src/lib/services/data-source-controller.service';
import { Category } from '@rappider/rappider-sdk';

import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DATA_SOURCES, DataSourceConnector } from '../datasources/datasources.data';
import { BrowserStorageManagementService, NotificationService } from '@rappider/services';
import { FullPaths } from '@rappider/shared/definitions';
import { JoyrideModule, JoyrideService } from 'ngx-joyride';
import { DataSourceFormData } from '../../utils/data-source-form-data.interface';
import { environment } from '@environment';
import { getProjectModelsWithFields } from '@rappider/shared';
import { Store } from '@ngrx/store';
import { getCategories } from 'libs/shared/src/lib/states/category/utils/get-categories-selector'

@Component({
  selector: 'rappider-datasource-add-with-file',
  standalone: true,
  imports: [
    CommonModule,
    RappiderEditFormModule,
    JoyrideModule
  ],
  templateUrl: './datasource-add-with-file.component.html',
  styleUrls: ['./datasource-add-with-file.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RappiderDatasourceAddWithFileComponent implements OnInit, OnDestroy, AfterViewInit {
  /* if dataSourceId exists then ingest add new file to datasource instead of create new datasource */
  @Input() dataSourceId: string | null;

  @Output() successfullySubmitted = new EventEmitter<boolean>();

  fileDatasourceCreateFormConfig: CrudFormConfig | undefined;
  datasourceCount = 0;

  selectedDataSourceType: { name: string; description: string } | undefined;
  datasourceCreateOptions = DATA_SOURCES;

  predefinedData: { key: string; title: string } | undefined;

  dataSourceKey: string | null = null;

  subscriptions: Subscription[] = [];

  initialKey = '';
  initialTitle = '';
  submitButtonLoading = false;
  selectProjectModels: any;
  activeIngestDataStatus = 'IngestToTheDefaultDataTableOfTheDataSource';
  categories: Category[] = [];

  constructor(
    private entityService: EntityService,
    private loopbackCrudApiService: LoopbackCrudApiService,
    private dataSourceControllerService: DataSourceControllerService,
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private browserStorageManagementService: BrowserStorageManagementService,
    private readonly joyrideService: JoyrideService,
    private store: Store<any>,
  ) {
    // Constructor implementation
  }


  ngOnDestroy() {
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  ngOnInit() {
    this.getDatasourceCount();
    this.fileDatasourceCreateFormConfig = this.entityService.generateEntityConfig(
      DATA_SOURCE_CREATE_BY_FILE_META_CONFIG)?.config?.createItemFormConfig;
    this.subscriptions = [
      this.subscribeToRouteParameters(),
      this.subscribeToProjectModels(),
      this.subscribeToCategories()
    ];
  }

  ngAfterViewInit() {

    this.cd.markForCheck();
  }

  onRenderCompleted(renderCompleted: boolean) {
    if (renderCompleted === true) {
      if (this.browserStorageManagementService.getLocalStorageItem('tour') === 'seventh') {
        this.showSteps();
      }
    }
  }

  subscribeToCategories() {
    return this.store.select(getCategories).subscribe((categories: Category[]) => {
      this.categories = categories;
      if (this.categories?.length) {
        const selectCategory = this.fileDatasourceCreateFormConfig?.items.find(item => item.fieldName === 'categoryId');
        selectCategory.options = this.categories?.map(category => ({
          key: category?.title || category?.name,
          value: category.id
        }));
        this.fileDatasourceCreateFormConfig = { ...this.fileDatasourceCreateFormConfig };
      }
    });
  }

  subscribeToRouteParameters() {
    return this.route.paramMap.subscribe((params: ParamMap) => {
      // Use the .get() method with the same name you defined in the route path
      this.dataSourceKey = params.get('dataSourceKey');
      this.selectedDataSourceType = this.datasourceCreateOptions.find(item => item.key === this.dataSourceKey);
      if (this.dataSourceKey === 'file-storage-add-multiple' || this.dataSourceId) {
        const file = this.fileDatasourceCreateFormConfig?.items.find(item => item.fieldName === 'file');
        file.type = CrudViewFormItemType.MultipleFileUpload;
        (file as any).config = {
          acceptedFileTypes: '.csv, .txt, .json, .png, .jpg, .jpeg, .pdf, .doc, .docx, .xls, .xlsx, .md, .xml',
          uploadText: 'Upload',
          maxFileCount: environment.maxFileCount ?? 5,
          maxFileSize: environment.maxFileSize ?? 102400,
          showFileList: true,
          listType: 'picture',
          maxHeight: '200px',
          scrollable: true,
          previewVisibility: false
        }
      }
      if (this.dataSourceId && this.fileDatasourceCreateFormConfig?.items) {
        this.fileDatasourceCreateFormConfig.items = this.fileDatasourceCreateFormConfig?.items?.filter(item => (item.fieldName !== 'title' && item.fieldName !== 'key' && item.fieldName !== 'description'));
        this.fileDatasourceCreateFormConfig.submitButton.text = 'Add File to Data Source';
      }
      // Now you can use this.dataSourceKey to fetch data
    });
  }

  subscribeToProjectModels() {
    return this.store.select(<any>getProjectModelsWithFields).subscribe(projectModels => {
      this.selectProjectModels = projectModels?.map(projectModel => ({
        key: projectModel?.title || projectModel?.name,
        value: projectModel?.id
      }));
      if (this.selectProjectModels?.length) {
        const selectProjectModel = this.fileDatasourceCreateFormConfig?.items.find(item => item.fieldName === 'selectProjectModel');
        selectProjectModel.options = this.selectProjectModels;
      }
    });
  }

  getDatasourceCount() {
    const url = this.loopbackCrudApiService.getDefaultBaseApiUrl() + ('/data-sources/count');
    this.loopbackCrudApiService.count({ url }).subscribe({
      next: (response: { count: number }) => {
        console.log('Datasource Count:', response.count);
        this.setPredefinedData(+response?.count);
      },
      error: (error: any) => {
        console.error('Error fetching datasource count:', error);
        this.setPredefinedData(0);
      }
    });
  }

  setPredefinedData(datasourceCount: number) {
    this.datasourceCount = datasourceCount || 0;
    this.initialKey = `DataSource${this.datasourceCount + 1}`;
    this.initialTitle = `Data Source ${this.datasourceCount + 1}`;
    this.predefinedData = {
      key: this.initialKey,
      title: this.initialTitle
    };
    this.cd.markForCheck();
  }

  onFileCreateFormSubmit__V1(datasource: DataSourceFormData) {
    // Handle the form submission for creating a new datasource
    console.log('Datasource Created:', datasource);

    if (!datasource.file || !datasource.key || !datasource.title) {
      console.error('Missing required fields for datasource creation');
      return;
    }

    this.loopbackCrudApiService.ingestDataSource({
      url: `${this.loopbackCrudApiService.getDefaultBaseApiUrl()}/data-sources/ingest-v2`,
      file: datasource.file,
      dataSourceName: datasource.key,
      dataSourceTitle: datasource.title,
      dataSourceConfig: datasource.config || {},
      fileName: datasource.file.name || 'File'
    }).subscribe({
      next: (response: any) => {
        console.log('Datasource Ingested Successfully:', response);
      },
      error: (error: any) => {
        console.error('Error ingesting datasource:', error);
      }
    });
  }

  onFileCreateFormSubmit(datasource: DataSourceFormData) {
    if (this.dataSourceId) {
      this.submitButtonLoading = true;
      const params = {
        body: {
          files: datasource.file,
          body: { id: this.dataSourceId, connector: DataSourceConnector.File, isApproved: datasource.isApproved, categoryId: (datasource as any).categoryId },
          metadata: {}
        }
      }
      if (this.activeIngestDataStatus === 'ingestToAnewDataTable') {
        params.body.metadata = { key: 'createNewProjectModel', value: true }
      }
      if (this.activeIngestDataStatus === 'IngestToAnExistingDataTable') {
        params.body.metadata = { key: 'projectModelId', value: (datasource as any)?.selectProjectModel }
      }
      if (this.activeIngestDataStatus === 'IngestToTheDefaultDataTableOfTheDataSource') {
        params.body.metadata = { key: 'createNewProjectModel', value: false }
      }
      this.dataSourceControllerService.ingestV2(<any>params).subscribe({
        next: (response: any) => {
          this.submitButtonLoading = false;
          this.cd.markForCheck();
          console.log('Datasource Ingested Successfully:', response);

          this.notificationService.createNotification(
            'success',
            'Data Source Created Successfully',
            'Your data source has been successfully ingested and is ready to use.'
          );
          this.successfullySubmitted.emit(true);
        },
        error: (error: any) => {
          this.submitButtonLoading = false;
          this.cd.markForCheck();
          console.error('Error ingesting datasource:', error);
          this.notificationService.createNotification(
            'error',
            'Error ingesting datasource',
            error?.error?.error?.message || 'An error occurred while ingesting the datasource.');
        }
      });
    } else {
      if (!datasource?.file || !this.validateFile(datasource.file)) {
        return;
      }

      if (!datasource.file || !datasource.key || !datasource.title) {
        return;
      }

      // Handle the form submission for creating a new datasource
      this.submitButtonLoading = true;
      this.cd.markForCheck();

      console.log('Datasource Created:', datasource);
      const params = {
        body: {
          files: datasource.file,
          body: { name: datasource.key, connector: DataSourceConnector.File, title: datasource.title, description: datasource?.description, isApproved: datasource.isApproved, categoryId: (datasource as any).categoryId },
          metadata: {}
        }
      }
      if (this.activeIngestDataStatus === 'ingestToAnewDataTable') {
        params.body.metadata = { key: 'createNewProjectModel', value: true }
      }
      if (this.activeIngestDataStatus === 'IngestToAnExistingDataTable') {
        params.body.metadata = { key: 'projectModelId', value: (datasource as any)?.selectProjectModel }
      }
      if (this.activeIngestDataStatus === 'IngestToTheDefaultDataTableOfTheDataSource') {
        params.body.metadata = { key: 'createNewProjectModel', value: false }
      }
      this.dataSourceControllerService.ingestV2(<any>params).subscribe({
        next: (response: any) => {
          this.submitButtonLoading = false;
          this.cd.markForCheck();
          console.log('Datasource Ingested Successfully:', response);

          this.notificationService.createNotification(
            'success',
            'Data Source Created Successfully',
            'Your data source has been successfully ingested and is ready to use.'
          );

          // Navigate to the datasources page after successful ingestion
          this.router.navigate([FullPaths.AIDataSources], { queryParams: { entity: 'DataSource' } });
        },
        error: (error: any) => {
          this.submitButtonLoading = false;
          this.cd.markForCheck();
          console.error('Error ingesting datasource:', error);
          this.notificationService.createNotification(
            'error',
            'Error ingesting datasource',
            error?.error?.error?.message || 'An error occurred while ingesting the datasource.');
        }
      });
    }
  }

  onFileCreateFormValueChange(value: DataSourceFormData) {
    // Handle form value changes if needed
    console.log('Form Value Changed:', value);

    // if there is a file and also if the key and title was not changed, then set the key and title from the file name
    if (value?.file?.name && value?.key === this.initialKey && value?.title === this.initialTitle) {
      const fileName = value?.file?.name;
      if (fileName) {
        const fileNameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
        // key is camel case

        // also replace any special char and -; allow _
        const newKey = this.entityService.convertToPascalCase(fileNameWithoutExtension).replace(/[^a-zA-Z0-9_]/g, '').replace(/-/g, '');
        if (newKey) {
          value.key = newKey;
          this.initialKey = newKey; // update initial key so that if file is changed, it will update the key again
        }

        // title is title case
        const newTitle = this.entityService.convertToTitleCase(fileNameWithoutExtension);
        if (newTitle) {
          value.title = newTitle;
          this.initialTitle = newTitle; // update initial title so that if file is changed, it will update the title again
        }
        this.predefinedData = {
          ...this.predefinedData,
          key: newKey,
          title: newTitle
        };
        this.cd.markForCheck();

      }
    }
    if (this.activeIngestDataStatus !== (value as any)?.ingestDataTo) {
      if ((value as any)?.ingestDataTo === 'IngestToAnExistingDataTable') {
        const selectProjectModelVisible = this.fileDatasourceCreateFormConfig.items?.find(item => item.fieldName === 'selectProjectModel');
        selectProjectModelVisible.visible = true;
        this.fileDatasourceCreateFormConfig = { ...this.fileDatasourceCreateFormConfig };
      } else if ((value as any)?.ingestDataTo === 'ingestToAnewDataTable' || (value as any)?.ingestDataTo === 'IngestToTheDefaultDataTableOfTheDataSource') {
        const selectProjectModelVisible = this.fileDatasourceCreateFormConfig.items?.find(item => item.fieldName === 'selectProjectModel');
        selectProjectModelVisible.visible = false;
        this.fileDatasourceCreateFormConfig = { ...this.fileDatasourceCreateFormConfig };
      }
    }
    this.activeIngestDataStatus = (value as any)?.ingestDataTo;
  }

  showSteps() {
    this.joyrideService.startTour(
      {
        steps: ['firstOptional', 'secondOptional', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'nineth'],
        themeColor: '#2d2f34',
        startWith: 'seventh',
        waitingTime: 500,
        customTexts: { next: 'Next', prev: 'Prev', done: 'Done' },
        showPrevButton: false
      }
    ).subscribe(
      () => {
        // Step completed
      },
      () => {
        // Error occurred
      },
      () => {
        // Tour completed
      }
    );
  }

  onNextClick(data: string) {
    if (data === 'seventh' && (this.browserStorageManagementService.getLocalStorageItem('tour') !== 'nineth')) {
      this.browserStorageManagementService.setLocalStorageItem('tour', 'eighth');
      this.router.navigate([FullPaths.AIDataSources], { queryParams: { entity: 'DataSource' } });
    }
  }

  onJoyrideDoneButtonClick() {
    this.browserStorageManagementService.setLocalStorageItem('tour', 'stop');
  }

  /**
   * Validates the uploaded file before submission
   * @param file The file to validate
   * @returns true if file is valid, false otherwise
   */
  private validateFile(file: File): boolean {
    if (!file) {
      this.notificationService.createNotification(
        'error',
        'No File Selected',
        'Please select a file to upload.'
      );
      return false;
    }

    // Check if file is empty (0 bytes)
    if (file.size === 0) {
      this.notificationService.createNotification(
        'error',
        'Empty File',
        'The selected file is empty. Please select a file with content.'
      );
      return false;
    }

    return true;
  }
}
